/** 取数 hooks。没有引入数据层库 —— 这套界面的查询都很直白，一个 useAsync 足够。 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { request, type ListResponse, type RequestOptions } from './api.ts';

export interface AsyncState<T> {
  readonly data: T | null;
  readonly error: unknown;
  readonly loading: boolean;
  readonly reload: () => void;
}

/**
 * 拉一个接口。`deps` 变化时重新拉。
 *
 * 用一个自增的 `seq` 丢弃过期响应：切换筛选条件时会连着发好几个请求，
 * 它们的返回顺序**不保证**和发出顺序一致。没有这一步的话，
 * 一个慢的旧请求可能后到并覆盖掉新条件的结果 —— 表格里显示的是上一次筛选的数据，
 * 而筛选框显示的是新条件，对不上但也不报错，是最难查的一类问题。
 */
export function useAsync<T>(
  path: string | null,
  options: RequestOptions = {},
  deps: readonly unknown[] = [],
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(path !== null);
  const [nonce, setNonce] = useState(0);
  const seqRef = useRef(0);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (path === null) {
      setData(null);
      setLoading(false);
      return;
    }
    const seq = ++seqRef.current;
    setLoading(true);
    setError(null);

    void request<T>(path, optionsRef.current)
      .then((result) => {
        if (seq !== seqRef.current) return; // 过期响应，丢弃
        setData(result);
        setLoading(false);
      })
      .catch((cause: unknown) => {
        if (seq !== seqRef.current) return;
        setError(cause);
        setLoading(false);
      });
    // options 走 ref，故意不进依赖：它每次渲染都是新对象，进去会导致无限循环
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce, ...deps]);

  const reload = useCallback(() => { setNonce((value) => value + 1); }, []);

  return { data, error, loading, reload };
}

/**
 * 游标分页列表。
 *
 * 只前进不后退 —— 后端的游标是「上一页最后一条的 id」，天然只支持向后翻。
 * 想回上一页就把游标栈存下来，这里用 `cursors` 数组做到了。
 */
export function usePagedList<T>(
  path: string,
  query: Record<string, string | number | null | undefined>,
  limit = 25,
): {
  readonly items: readonly T[];
  readonly error: unknown;
  readonly loading: boolean;
  readonly page: number;
  readonly hasNext: boolean;
  readonly hasPrev: boolean;
  readonly next: () => void;
  readonly prev: () => void;
  readonly reload: () => void;
} {
  const [cursors, setCursors] = useState<readonly (string | null)[]>([null]);
  const [page, setPage] = useState(0);
  const queryKey = JSON.stringify(query);

  // 筛选条件一变就回到第一页：留在第 3 页却换了条件，游标指向的是旧结果集里的记录
  useEffect(() => {
    setCursors([null]);
    setPage(0);
  }, [queryKey]);

  const cursor = cursors[page] ?? null;
  const state = useAsync<ListResponse<T>>(
    path,
    { query: { ...query, limit, cursor } },
    [queryKey, cursor, limit],
  );

  const nextCursor = state.data?.next_cursor ?? null;

  return {
    items: state.data?.data ?? [],
    error: state.error,
    loading: state.loading,
    page,
    hasNext: nextCursor !== null,
    hasPrev: page > 0,
    next: () => {
      if (nextCursor === null) return;
      setCursors((prev) => (page + 1 < prev.length ? prev : [...prev, nextCursor]));
      setPage((value) => value + 1);
    },
    prev: () => { setPage((value) => Math.max(0, value - 1)); },
    reload: state.reload,
  };
}

/** 写操作的通用状态机：pending / error，成功后回调。 */
export function useMutation<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
): {
  readonly run: (...args: TArgs) => Promise<TResult | null>;
  readonly pending: boolean;
  readonly error: unknown;
  readonly reset: () => void;
} {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const run = useCallback(
    async (...args: TArgs): Promise<TResult | null> => {
      setPending(true);
      setError(null);
      try {
        return await fn(...args);
      } catch (cause) {
        setError(cause);
        return null;
      } finally {
        setPending(false);
      }
    },
    [fn],
  );

  return { run, pending, error, reset: () => { setError(null); } };
}
